/**
 * PATH: packages/ui/src/contextMenu/positionedShared.ts
 * FILE_ID: UI-CTXMENU-POSITIONED-SHARED-001
 * PURPOSE: Constants + context shared by every PositionedContextMenu split
 *          module (change #269 D4 500-line ceiling split) — the z-index
 *          scale, the submenu hover-close grace period, and the context
 *          that lets a portaled <ContextMenuSub> panel inherit the root's
 *          z-scale without prop-drilling.
 * ROLE IN ARCHITECTURE: Shared primitives layer below ContextMenuRoot.tsx /
 *          ContextMenuSub.tsx / ContextMenuPrimitives.tsx.
 * MAIN EXPORTS: DEFAULT_Z_INDEX_BASE, SUB_HOVER_CLOSE_DELAY_MS,
 *          MenuZIndexContext, ContextMenuIcon.
 * NON-RESPONSIBILITIES: No components — pure constants/context only.
 */
import { createContext, type ComponentType } from 'react';

/** Grace period (ms) before a hover-opened submenu collapses after the
 *  cursor leaves both the trigger AND the panel. ~180ms is long enough
 *  to cross the (zero) gap on a slow drag, short enough that an
 *  unrelated mouse movement doesn't leave panels open. */
export const SUB_HOVER_CLOSE_DELAY_MS = 180;

/** Overlay scale (UX audit 2026-06, Layout #4): drawers z-[100] <
 *  context menus (base backdrop, +1 root panel, +2 portaled submenu
 *  panels) < toasts z-[120]. Surfaces with taller overlay stacks (the
 *  bravada takeoff overlay sits at z-[200]) pass a higher base via the
 *  `zIndexBase` prop. */
export const DEFAULT_Z_INDEX_BASE = 110;

/** Lets <ContextMenuSub> portaled panels inherit the root's z-scale
 *  without prop-drilling through arbitrary children. */
export const MenuZIndexContext = createContext(DEFAULT_Z_INDEX_BASE);

/** Widest icon contract of the four merged copies (bravada's): any
 *  component taking className — every lucide icon satisfies it. */
export type ContextMenuIcon = ComponentType<{ className?: string }>;

/** Shared base class every menu row (item + sub-trigger) renders with. */
export const menuRowBaseCls =
  'flex items-center gap-2 w-full px-3 py-1.5 text-xs text-left rounded-md transition-colors';
